import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import parse from 'html-react-parser';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { MultiSelect } from 'primereact/multiselect';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';
import { addLocale } from 'primereact/api';
import { deleteRequest, postRequest } from '../../api/ApiController';
import AuthContext from '../AuthContext';
import MyEditor from '../Editor';
import { LocaleContext } from '../LocaleContext';
import UtilContext from '../UtilContext';
import { expandSystemI18nParams } from '../../utils/systemI18nComment';
import { BotIcon } from '../../pages/svg/svgIcons';

function buildPrimeReactCalendarLocale(calLocale, t) {
  const monthLongFmt = new Intl.DateTimeFormat(calLocale, { month: 'long', timeZone: 'UTC' });
  const monthShortFmt = new Intl.DateTimeFormat(calLocale, { month: 'short', timeZone: 'UTC' });
  const wdLongFmt = new Intl.DateTimeFormat(calLocale, { weekday: 'long', timeZone: 'UTC' });
  const wdShortFmt = new Intl.DateTimeFormat(calLocale, { weekday: 'short', timeZone: 'UTC' });
  const wdNarrowFmt = new Intl.DateTimeFormat(calLocale, { weekday: 'narrow', timeZone: 'UTC' });
  const sundayUtc = new Date(Date.UTC(2000, 0, 2));
  const dayNames = [];
  const dayNamesShort = [];
  const dayNamesMin = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(sundayUtc);
    d.setUTCDate(sundayUtc.getUTCDate() + i);
    dayNames.push(wdLongFmt.format(d));
    dayNamesShort.push(wdShortFmt.format(d));
    dayNamesMin.push(wdNarrowFmt.format(d));
  }
  const monthNames = [];
  const monthNamesShort = [];
  for (let m = 0; m < 12; m += 1) {
    const d = new Date(Date.UTC(2000, m, 1));
    monthNames.push(monthLongFmt.format(d));
    monthNamesShort.push(monthShortFmt.format(d));
  }
  return {
    firstDayOfWeek: 1,
    dayNames,
    dayNamesShort,
    dayNamesMin,
    monthNames,
    monthNamesShort,
    today: t('Common.Today'),
    clear: t('Common.Clear'),
    chooseDate: t('Common.ChooseDate'),
  };
}

function onlyUniqueMentionId(value, index, array) {
  return array.indexOf(value) === index;
}

function loopThroughDOMForMentions(obj, findedIds) {
  if (obj == null) return;
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i += 1) {
      loopThroughDOMForMentions(obj[i], findedIds);
    }
    return;
  }
  if (typeof obj === 'object' && obj.props) {
    if (Object.prototype.hasOwnProperty.call(obj.props, 'children') && obj.props.children !== undefined) {
      loopThroughDOMForMentions(obj.props.children, findedIds);
    }
    if (Object.prototype.hasOwnProperty.call(obj.props, 'data-id')) {
      findedIds.push(obj.props['data-id']);
    }
  }
}

function extractMentionIdsFromHtml(description) {
  const findedIds = [];
  try {
    const tree = parse(description || '', { trim: true });
    loopThroughDOMForMentions(tree, findedIds);
  } catch (_) {
    /* ignore parse errors */
  }
  return findedIds.filter(onlyUniqueMentionId).filter((id) => id != null && String(id).trim() !== '').join(',');
}

function assertCustomerDetailApiOk(resp, fallbackMessage) {
  if (resp?.errors && typeof resp.errors === 'object') {
    const firstKey = Object.keys(resp.errors)[0];
    const arr = firstKey ? resp.errors[firstKey] : null;
    const msg = Array.isArray(arr) && arr[0] != null ? String(arr[0]) : null;
    throw new Error(msg || resp?.message || fallbackMessage);
  }
  const st = resp?.status;
  if (typeof st === 'number' && st >= 400) {
    throw new Error(resp?.message || fallbackMessage);
  }
}

function renderCrmMyCustomerSystemCommentBody(rawDescription, t) {
  const raw = String(rawDescription ?? '').trim();
  if (raw.startsWith('{')) {
    try {
      const j = JSON.parse(raw);
      if (j?._systemI18n?.key) {
        const params = j._systemI18n.params && typeof j._systemI18n.params === 'object' ? j._systemI18n.params : {};
        const expanded = expandSystemI18nParams(t, params);
        return <span className="crm-system-activity-text">{t(String(j._systemI18n.key), expanded)}</span>;
      }
      const key = j?._crmActivity?.key;
      const params = j?._crmActivity?.params && typeof j._crmActivity.params === 'object' ? j._crmActivity.params : {};
      if (key) {
        return <span className="crm-system-activity-text">{t(`CustomerMyCustomersDetail.SystemActivity.${key}`, params)}</span>;
      }
    } catch (_) {
      /* fall through */
    }
  }
  return <span className="crm-system-activity-html">{parse(raw || '')}</span>;
}

function removeDiacritics(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function formatDateForApi(value) {
  if (!value) return '';
  const dt = DateTime.fromJSDate(value);
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : '';
}

function crmRoleLabel(roleKey, t) {
  if (roleKey === 'supervisor') return t('CustomerMyCustomersDetail.Role.Supervisor');
  if (roleKey === 'trader') return t('CustomerMyCustomersDetail.Role.Trader');
  if (roleKey === 'interested') return t('CustomerMyCustomersDetail.Role.Interested');
  return '';
}

export default function CustomerBusinessStatusPanel({
  customerId,
  comments = [],
  mentions = [],
  executionUserId = null,
  deadline = null,
  deadlineReminderCommentId = null,
  usersForMentions = [],
  loading = false,
  disabled = false,
  canManageInterestedUsers = false,
  canRemoveInterestedUsers = false,
  afterSaveHint = '',
  title,
  intro,
  onDetailUpdated,
  onChangeExecutionUser,
  onChangeDeadline,
}) {
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { toast, formatDate, getUserInitials, getUserColor } = useContext(UtilContext);
  const [commentSaving, setCommentSaving] = useState(false);
  const [interestedUsersSaving, setInterestedUsersSaving] = useState(false);
  const [savingExecution, setSavingExecution] = useState(false);
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [bellReminderSaving, setBellReminderSaving] = useState(false);
  const [commentClick, setCommentClick] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addedUser, setAddedUser] = useState([]);
  const [updatableComment, setUpdatableComment] = useState(0);
  const [sortDescending, setSortDescending] = useState(true);
  const op = useRef(null);
  const calendarLocale = locale === 'gb' ? 'en' : (locale || 'sk');

  useEffect(() => {
    addLocale(calendarLocale, buildPrimeReactCalendarLocale(calendarLocale, t));
  }, [calendarLocale, t]);

  const lastAddedComment = useMemo(() => {
    if (!comments.length) return null;
    return comments.reduce((latest, current) => ((current?.id || 0) > (latest?.id || 0) ? current : latest), comments[0]);
  }, [comments]);

  const mentionSelectOptions = useMemo(() => {
    const mentionIds = new Set((mentions || []).map((m) => Number(m.id)));
    return (usersForMentions || [])
      .filter((u) => !mentionIds.has(Number(u.id)))
      .map((u) => ({
        name: u?.employee?.name || u?.username || `ID ${u.id}`,
        id: u.id,
        searchLabel: removeDiacritics(u?.employee?.name || u?.username || ''),
      }));
  }, [mentions, usersForMentions]);

  const executionOptions = useMemo(() => {
    const selectedId = Number(executionUserId || 0) || null;
    const rows = (usersForMentions || []).map((u) => ({
      label: u?.employee?.name || u?.username || `ID ${u.id}`,
      value: Number(u.id),
      searchLabel: removeDiacritics(u?.employee?.name || u?.username || ''),
    }));
    if (selectedId && !rows.some((u) => Number(u.value) === selectedId)) {
      const selectedMention = (mentions || []).find((m) => Number(m.id) === selectedId);
      rows.unshift({
        label: selectedMention?.employee?.name || selectedMention?.username || `ID ${selectedId}`,
        value: selectedId,
        searchLabel: removeDiacritics(selectedMention?.employee?.name || selectedMention?.username || ''),
      });
    }
    return rows;
  }, [executionUserId, mentions, usersForMentions]);

  const formikComment = useFormik({
    initialValues: { description: '', files: [] },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!customerId || !user?.id) return;
      setCommentSaving(true);
      try {
        const fd = new FormData();
        fd.append('description', values.description || '');
        fd.append('contractor_id', String(user.id));
        fd.append('mentions', extractMentionIdsFromHtml(values.description || ''));
        const resp = await postRequest(`/api/customer_evaluations/my_customers/${customerId}/comments`, fd, true);
        assertCustomerDetailApiOk(resp, t('ServiceTask.SaveFailed'));
        onDetailUpdated?.(resp?.data ?? resp);
        formikComment.resetForm();
        setCommentClick(false);
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerMyCustomersDetail.Toast.CommentSaved'), life: 2500 });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
      } finally {
        setCommentSaving(false);
      }
    },
  });

  const formikUpdateComment = useFormik({
    initialValues: { id: null, description: '', files: [] },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!customerId || !values.id) return;
      setCommentSaving(true);
      try {
        const fd = new FormData();
        fd.append('description', values.description || '');
        fd.append('mentions', extractMentionIdsFromHtml(values.description || ''));
        const resp = await postRequest(`/api/customer_evaluations/my_customers/${customerId}/comments/update/${values.id}`, fd, true);
        assertCustomerDetailApiOk(resp, t('ServiceTask.SaveFailed'));
        onDetailUpdated?.(resp?.data ?? resp);
        formikUpdateComment.resetForm();
        setUpdatableComment(0);
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerMyCustomersDetail.Toast.CommentUpdated'), life: 2500 });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
      } finally {
        setCommentSaving(false);
      }
    },
  });

  const addInterestedUsers = useCallback(async () => {
    if (!customerId || !addedUser?.length) return;
    setInterestedUsersSaving(true);
    try {
      const fd = new FormData();
      const ids = addedUser
        .map((u) => Number(typeof u === 'object' && u != null ? u.id : u))
        .filter((n) => Number.isFinite(n) && n > 0);
      fd.append('mention', ids.join(','));
      const resp = await postRequest(`/api/customer_evaluations/my_customers/${customerId}/addUser`, fd, true);
      assertCustomerDetailApiOk(resp, t('ServiceTask.SaveFailed'));
      onDetailUpdated?.(resp?.data ?? resp);
      setAddedUser([]);
      setShowAddUser(false);
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerMyCustomersDetail.Toast.UsersAdded'), life: 2500 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setInterestedUsersSaving(false);
    }
  }, [addedUser, customerId, onDetailUpdated, t, toast]);

  const removeInterestedUser = useCallback(async (removeUserId) => {
    const uid = Number(removeUserId || 0);
    if (!customerId || uid <= 0) return;
    setInterestedUsersSaving(true);
    try {
      const resp = await deleteRequest(`/api/customer_evaluations/my_customers/${customerId}/users/${uid}`);
      assertCustomerDetailApiOk(resp, t('ServiceTask.SaveFailed'));
      onDetailUpdated?.(resp?.data ?? resp);
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerMyCustomersDetail.Toast.UserRemoved'), life: 2500 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setInterestedUsersSaving(false);
    }
  }, [customerId, onDetailUpdated, t, toast]);

  const updateExecutionUser = useCallback(async (value) => {
    if (!customerId || !onChangeExecutionUser) return;
    setSavingExecution(true);
    try {
      const payload = await onChangeExecutionUser(value || null);
      if (payload) onDetailUpdated?.(payload);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setSavingExecution(false);
    }
  }, [customerId, onChangeExecutionUser, onDetailUpdated, t, toast]);

  const updateDeadline = useCallback(async (value) => {
    if (!customerId || !onChangeDeadline) return;
    setSavingDeadline(true);
    try {
      const payload = await onChangeDeadline(formatDateForApi(value));
      if (payload) onDetailUpdated?.(payload);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setSavingDeadline(false);
    }
  }, [customerId, onChangeDeadline, onDetailUpdated, t, toast]);

  const toggleDeadlineReminderComment = useCallback(async (comment) => {
    const commentId = Number(comment?.id || 0);
    if (!customerId || commentId <= 0 || Number(comment?.by_system) !== 0) return;
    const markedId = Number(deadlineReminderCommentId || 0);
    const isActive = markedId === commentId;
    setBellReminderSaving(true);
    try {
      const resp = await postRequest(
        `/api/customer_evaluations/my_customers/${customerId}/deadline_reminder_comment`,
        { comment_id: isActive ? null : commentId },
      );
      assertCustomerDetailApiOk(resp, t('ServiceTask.SaveFailed'));
      onDetailUpdated?.(resp?.data ?? resp);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setBellReminderSaving(false);
    }
  }, [customerId, deadlineReminderCommentId, onDetailUpdated, t, toast]);

  const renderRoleLabels = useCallback((roles) => {
    const labels = (Array.isArray(roles) ? roles : [])
      .map((role) => crmRoleLabel(role, t))
      .filter(Boolean);
    if (!labels.length) return null;
    return <span className="text-xs text-gray-500">{labels.join(', ')}</span>;
  }, [t]);

  const renderCommentComposer = () => (
    commentClick ? (
      <div className="mt-2 comment-add-block">
        <div className="app-activity-comment-editor-border-wrap">
          <MyEditor editorValue={formikComment.values.description} autofocus fileAccept="*" formik={formikComment} mentionUsers={usersForMentions} activityMessageRadius={true} />
        </div>
        <div className="flex flex-row gap-1 mt-2">
          <button type="button" disabled={commentSaving} onClick={() => { setCommentClick(false); formikComment.resetForm(); }} className="flex justify-center items-center w-full h-10 px-2 py-1 text-white bg-gray-600 rounded-md hover:bg-gray-400 disabled:opacity-40">{t('Common.Cancel')}</button>
          <button type="button" disabled={commentSaving} onClick={() => formikComment.submitForm()} className="flex justify-center items-center w-full h-10 px-2 py-1 text-white bg-zinc-900 rounded-md hover:bg-zinc-700 disabled:opacity-40">{commentSaving ? t('Common.Saving') : t('Common.Save')}</button>
        </div>
      </div>
    ) : (
      <div className="flex flex-row mt-3">
        <div className="basis-1/10">
          <Avatar className="p-overlay-badge" title={user?.employee?.name || user?.username} label={getUserInitials(user?.employee?.name || user?.username)} shape="circle" style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff', marginRight: '5px' }} />
        </div>
        <div className="flex flex-col basis-9/10">
          <input type="text" onClick={() => setCommentClick(true)} placeholder={t('CustomerMyCustomersDetail.AddCommentPlaceholder')} className="app-activity-add-comment-input px-1 py-1 border focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40" readOnly />
        </div>
      </div>
    )
  );

  return (
    <>
      <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
        <div className="gap-7 bg-white p-2 border rounded-md">
          <div className="mb-3">
            <div className="flex justify-between">
              <div className="mb-4 min-w-0 pr-2">
                <h2 className="text-xl font-semibold text-black">{title || t('CustomerMyCustomersDetail.BusinessCustomerStateTitle')}</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">{intro || t('CustomerMyCustomersDetail.BusinessCustomerStateIntro')}</p>
              </div>
              <button type="button" disabled={loading || !customerId} onClick={() => setSortDescending((prev) => !prev)} title={sortDescending ? t('CustomerMyCustomersDetail.SortOldestFirst') : t('CustomerMyCustomersDetail.SortNewestFirst')} className="text-gray-600 hover:text-gray-800 shrink-0 self-start disabled:opacity-40">
                <i className={`pi ${sortDescending ? 'pi-sort-amount-down' : 'pi-sort-amount-up'}`} />
              </button>
            </div>
            <div className="h-fit max-h-[420px] overflow-y-auto">
              {!customerId ? (
                <p className="text-sm text-gray-600">{afterSaveHint}</p>
              ) : loading ? (
                <>
                  {[0, 1, 2].map((k) => (
                    <div key={`sk-c-${k}`} className="flex flex-row w-full gap-3 mb-4">
                      <Skeleton shape="circle" size="2.5rem" className="shrink-0" />
                      <div className="flex-1 flex flex-col gap-2 min-w-0">
                        <Skeleton height="0.85rem" width="45%" />
                        <Skeleton height="2.75rem" width="100%" />
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {sortDescending ? renderCommentComposer() : null}
                  {(comments || []).slice().sort((a, b) => (sortDescending ? (b.id || 0) - (a.id || 0) : (a.id || 0) - (b.id || 0))).map((comment) => {
                    const isHumanComment = Number(comment.by_system) === 0;
                    const isReminderActive = isHumanComment && (
                      comment.is_deadline_reminder === true
                      || Number(deadlineReminderCommentId || 0) === Number(comment.id)
                    );
                    const showReminderBell = isHumanComment && !!deadline && !!customerId && !disabled;
                    return (
                    <div key={comment.id} className="group/crm-comment flex flex-row w-full justify-between mb-3">
                      <div className="basis-1/10">
                        {Number(comment.by_system) < 2 ? (
                          <Avatar className="p-overlay-badge" title={comment.contractor?.employee?.name || comment.contractor?.username} label={getUserInitials(comment.contractor?.employee?.name || comment.contractor?.username)} shape="circle" style={{ backgroundColor: getUserColor(comment.contractor?.username), color: '#ffffff', marginRight: '5px' }} />
                        ) : (
                          <Avatar className="p-overlay-badge" title="System" icon="pi pi-database" shape="circle" style={{ backgroundColor: 'grey', color: '#ffffff', marginRight: '5px' }} />
                        )}
                      </div>
                      <div className="flex flex-col grow basis-8/10 min-w-0">
                        {Number(comment.by_system) < 2 ? (
                          <div className="mb-1 flex flex-row items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex flex-col">
                                <span className="font-semibold">{comment.contractor?.employee?.name || comment.contractor?.username}</span>
                                {renderRoleLabels(comment.contractor?.crm_roles)}
                              </div>
                              <span className="text-sm text-gray-600">
                                {formatDate ? formatDate(comment.updated_at, false) : ''}
                                {comment.updated_at ? ` o ${DateTime.fromISO(String(comment.updated_at)).toFormat('HH:mm')}` : ''}
                              </span>
                            </div>
                            {showReminderBell ? (
                              <button
                                type="button"
                                title={isReminderActive
                                  ? t('CustomerBusinessStatusPanel.DeadlineReminderBellOff')
                                  : t('CustomerBusinessStatusPanel.DeadlineReminderBellOn')}
                                disabled={loading || bellReminderSaving}
                                onClick={() => toggleDeadlineReminderComment(comment)}
                                className={`shrink-0 p-1 rounded-md transition-opacity disabled:opacity-40 ${
                                  isReminderActive
                                    ? 'opacity-100 text-amber-600 hover:text-amber-700'
                                    : 'opacity-0 group-hover/crm-comment:opacity-100 text-gray-400 hover:text-amber-600'
                                }`}
                              >
                                <i className="pi pi-bell" style={{ fontSize: '1.1rem' }} />
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <div className="mb-1">
                            <span className="font-semibold">{t('CustomerMyCustomersDetail.SystemNotification')}</span>
                            {' '}
                            {formatDate ? formatDate(comment.updated_at, false) : ''}
                            {comment.updated_at ? ` o ${DateTime.fromISO(String(comment.updated_at)).toFormat('HH:mm')}` : ''}
                          </div>
                        )}
                        {Number(comment.by_system) === 0 ? (
                          <div>
                            {(comment.id === lastAddedComment?.id && updatableComment) ? (
                              <div className="comment-edit-block">
                                <div className="app-activity-comment-editor-border-wrap">
                                <MyEditor editorValue={formikUpdateComment.values.description} autofocus fileAccept="*" formik={formikUpdateComment} mentionUsers={usersForMentions} activityMessageRadius={true} />
                                </div>
                                <div className="flex flex-row gap-1 mt-2">
                                  <button type="button" disabled={commentSaving} onClick={() => { setUpdatableComment(0); formikUpdateComment.resetForm(); }} className="flex justify-center items-center w-full h-10 px-2 py-1 text-white bg-gray-600 rounded-md hover:bg-gray-400 disabled:opacity-40">{t('Common.Cancel')}</button>
                                  <button type="button" disabled={commentSaving} onClick={() => formikUpdateComment.submitForm()} className="flex justify-center items-center w-full h-10 px-2 py-1 text-white bg-zinc-900 rounded-md hover:bg-zinc-700 disabled:opacity-40">{commentSaving ? t('Common.Saving') : t('Common.Save')}</button>
                                </div>
                              </div>
                            ) : (
                              <MyEditor editorValue={String(comment.description || '')} hideToolbar hideFileUpload compact formik={null} activityMessageRadius={true} />
                            )}
                          </div>
                        ) : (
                          <Message severity="info" content={() => (
                            <div className="flex align-items-center">
                              <BotIcon />
                              <div className="ml-2">{renderCrmMyCustomerSystemCommentBody(comment.description, t)}</div>
                            </div>
                          )} className="justify-start bg-gray-100" />
                        )}
                      </div>
                      {(Number(comment.by_system) === 0 && comment.id === lastAddedComment?.id && Number(comment.contractor_id) === Number(user?.id)) ? (
                        <div className="flex basis-1/10">
                          {!updatableComment && (
                            <button type="button" onClick={(e) => op.current?.toggle(e)}><i className="pi pi-ellipsis-v" /></button>
                          )}
                          <OverlayPanel ref={op}>
                            <div className="flex flex-col">
                              <button type="button" className="px-4 py-3 text-left hover:bg-gray-200" onClick={(e) => { formikUpdateComment.setFieldValue('id', comment.id); formikUpdateComment.setFieldValue('description', comment.description); formikUpdateComment.setFieldValue('files', []); setUpdatableComment(1); op.current?.toggle(e); }}>
                                <i className="pi pi-pencil mr-2" />
                                <span className="font-semibold">{t('Common.Edit')}</span>
                              </button>
                            </div>
                          </OverlayPanel>
                        </div>
                      ) : null}
                    </div>
                  );
                  })}
                  {!sortDescending ? renderCommentComposer() : null}
                </>
              )}
            </div>
          </div>
          <div className="border-t pt-4 mt-4 text-gray-800">
            <div className="flex flex-col gap-4">
              <div>
                <span className="font-bold mb-3 block text-gray-800">{t('CustomerMyCustomersDetail.InterestedUsers')}</span>
                {!customerId ? null : loading ? (
                  <div className="flex flex-col gap-2 mt-1">
                    <Skeleton height="2.25rem" width="82%" borderRadius="6px" />
                  </div>
                ) : (
                  <>
                    {(mentions || []).map((mu) => (
                      <div key={mu.id} className="flex items-center gap-2 text-gray-800 mb-2">
                        <Avatar className="p-overlay-badge" title={mu?.employee?.name || mu?.username} label={getUserInitials(mu?.employee?.name || mu?.username)} shape="circle" style={{ backgroundColor: getUserColor(mu?.username), color: '#ffffff', marginRight: '5px' }} />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span>{mu?.employee?.name || mu?.username}</span>
                          {renderRoleLabels(mu?.crm_roles)}
                        </div>
                        {canRemoveInterestedUsers && Number(mu?.id) !== Number(user?.id) ? (
                          <button
                            type="button"
                            disabled={interestedUsersSaving || disabled}
                            className="text-sm text-red-700 hover:underline disabled:opacity-40"
                            onClick={() => removeInterestedUser(mu.id)}
                          >
                            {t('Common.Delete')}
                          </button>
                        ) : null}
                      </div>
                    ))}
                    <div className="text-gray-800">
                      <button type="button" onClick={() => setShowAddUser(true)} className="flex items-center gap-2 disabled:opacity-25" disabled={!canManageInterestedUsers || disabled}>
                        <i className="pi pi-plus mr-2 px-1" style={{ fontSize: '1.5rem' }} />
                        {t('CustomerMyCustomersDetail.AddInterestedUser')}
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="font-bold mb-1 block text-gray-800">{t('CustomerBusinessStatusPanel.Execution')}</label>
                  <Dropdown
                    value={executionUserId ? Number(executionUserId) : null}
                    onChange={(e) => updateExecutionUser(e.value)}
                    options={executionOptions}
                    optionLabel="label"
                    optionValue="value"
                    filter
                    filterBy="searchLabel,label"
                    showClear
                    disabled={!customerId || disabled || loading || savingExecution}
                    placeholder={t('CustomerBusinessStatusPanel.ExecutionPlaceholder')}
                    className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                  {deadline ? (
                    <p className="text-xs text-gray-500 mt-2">
                      {t('CustomerBusinessStatusPanel.DeadlineReminderHint')}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="font-bold mb-1 block text-gray-800">{t('CustomerBusinessStatusPanel.Deadline')}</label>
                  <Calendar
                    value={deadline ? new Date(deadline) : null}
                    onChange={(e) => updateDeadline(e.value)}
                    showIcon
                    showButtonBar
                    disabled={!customerId || disabled || loading || savingDeadline}
                    dateFormat="dd.mm.yy"
                    firstDayOfWeek={1}
                    locale={calendarLocale}
                    style={{ border: '1px solid #e5e7eb', transition: 'background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s', borderRadius: '6px' }}
                    className="w-full mt-2"
                    panelClassName="crm-business-status-calendar-panel"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={t('CustomerMyCustomersDetail.Dialog.AddInterestedUsers')}
        visible={showAddUser}
        style={{ width: '70vw' }}
        onHide={() => { setShowAddUser(false); setAddedUser([]); }}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" className="h-10 px-4 py-2 text-sm border rounded-md" onClick={() => { setShowAddUser(false); setAddedUser([]); }} disabled={interestedUsersSaving}>{t('Common.Cancel')}</button>
            <button type="button" disabled={!addedUser?.length || interestedUsersSaving} className="h-10 px-4 py-2 text-sm bg-zinc-900 text-white rounded-md disabled:opacity-25" onClick={() => addInterestedUsers()}>{interestedUsersSaving ? t('Common.Saving') : t('Common.Save')}</button>
          </div>
        )}
      >
        <MultiSelect
          value={addedUser}
          onChange={(e) => setAddedUser(e.value || [])}
          options={mentionSelectOptions}
          optionLabel="name"
          dataKey="id"
          display="comma"
          filter
          filterBy="searchLabel"
          filterMatchMode="contains"
          filterPlaceholder={t('Common.Search')}
          placeholder={t('CustomerMyCustomersDetail.MultiSelectPlaceholder')}
          className="w-full px-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
        />
      </Dialog>
    </>
  );
}
